/** Serverzustand der Termine. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createAppointment,
  deleteAppointment,
  listAppointments,
  updateAppointment,
  type AppointmentCreate,
  type AppointmentFilters,
  type AppointmentUpdate,
} from './api';

export const appointmentKeys = {
  all: ['appointments'] as const,
  list: (filters: AppointmentFilters) => ['appointments', filters] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useAppointments(filters: AppointmentFilters, enabled = true) {
  return useQuery({
    queryKey: appointmentKeys.list(filters),
    queryFn: () => listAppointments(filters),
    enabled,
    retry: retryUnlessPermanent,
  });
}

/**
 * Nach jeder Aenderung wird der ganze Zweig entwertet.
 *
 * Ein Termin steht am Ticket, im Kalender und auf der Startseite -- in
 * welchen Zeitraeumen, haengt an Beginn und Ende. Das in der Oberflaeche
 * nachzubauen hiesse, die Abfrage des Servers ein zweites Mal zu schreiben.
 */
function useAppointmentMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useCreateAppointment() {
  return useAppointmentMutation((payload: AppointmentCreate) => createAppointment(payload));
}

export function useUpdateAppointment() {
  return useAppointmentMutation((variables: { id: string; payload: AppointmentUpdate }) =>
    updateAppointment(variables.id, variables.payload),
  );
}

export function useDeleteAppointment() {
  return useAppointmentMutation((id: string) => deleteAppointment(id));
}
